import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

// Global variables
let deferredPromptGlobal = null;
let isMobileGlobal = false;
let isStandaloneGlobal = false;
let isDesktopGlobal = false;

// Device detection
const checkIsMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const checkIsDesktop = () => {
  return !checkIsMobile();
};

const checkIsInstalled = () => {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
};

export function canInstallPWA() {
  if (checkIsInstalled()) return false;

  // Desktop browsers can trigger installation in different ways
  if (isDesktopGlobal) {
    return true;
  }

  return isMobileGlobal || !!deferredPromptGlobal;
}

export function showInstallPrompt() {
  // Handle desktop installation
  if (isDesktopGlobal) {
    if (deferredPromptGlobal) {
      deferredPromptGlobal.prompt();
      return deferredPromptGlobal.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted the install prompt");
        } else {
          console.log("User dismissed the install prompt");
        }
        deferredPromptGlobal = null;
        return choiceResult.outcome === "accepted";
      });
    } else {
      // Generic desktop instructions
      alert(
        "To install this app:\n\n" +
          "1. Look for the install button in your browser's address bar\n" +
          '2. Or click the "⋮" or "⋯" menu and select "Install PrintKaro.shop"\n' +
          "3. Follow your browser's installation prompts"
      );
      return Promise.resolve(false);
    }
  }

  // Mobile handling (existing code)
  if (deferredPromptGlobal) {
    deferredPromptGlobal.prompt();
    return deferredPromptGlobal.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
      } else {
        console.log("User dismissed the install prompt");
      }
      deferredPromptGlobal = null;
      return choiceResult.outcome === "accepted";
    });
  } else if (isMobileGlobal) {
    // iOS specific instructions
    alert(
      'To install this app:\n1. Tap the Share button\n2. Select "Add to Home Screen"\n3. Tap "Add" in the top right corner'
    );
    return Promise.resolve(false);
  }
  return Promise.resolve(false);
}

export default function InstallPrompt() {
  const { darkMode } = useTheme();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deviceType, setDeviceType] = useState("desktop");
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Initialize global variables
    isMobileGlobal = checkIsMobile();
    isDesktopGlobal = checkIsDesktop();
    isStandaloneGlobal = checkIsInstalled();

    setDeviceType(isMobileGlobal ? "mobile" : "desktop");
    setIsStandalone(isStandaloneGlobal);

    if (isStandaloneGlobal) {
      setShowPrompt(false);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptGlobal = e;
      setShowPrompt(true);

      // Notify all clients that install is available
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "INSTALL_AVAILABLE",
        });
      }
    };

    const handleAppInstalled = () => {
      console.log("App was installed");
      isStandaloneGlobal = true;
      setIsStandalone(true);
      setShowPrompt(false);
    };

    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === "CAN_INSTALL") {
        setShowPrompt(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    }

    // Show prompt for new users (mobile or desktop)
    if (!localStorage.getItem("installPromptShown")) {
      setShowPrompt(true);
      localStorage.setItem("installPromptShown", "true");
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      }
    };
  }, []);

  const handleInstallClick = async () => {
    const installed = await showInstallPrompt();
    if (installed) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Show again after 1 week if they dismiss
    setTimeout(() => {
      if (!isStandalone) setShowPrompt(true);
    }, 7 * 24 * 60 * 60 * 1000);
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={`fixed bottom-4 right-4 z-50 p-4 rounded-xl shadow-2xl ${
        darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
      }`}
      style={{ maxWidth: deviceType === "desktop" ? "380px" : "320px" }}
    >
      <div className="flex flex-col space-y-3">
        <h3 className="font-bold text-lg">Install PrintKaro.shop</h3>
        <p className="text-sm">
          {deviceType === "mobile"
            ? "Add to your home screen for quick access!"
            : "Install this app on your computer for a better experience."}
        </p>
        <div className="flex space-x-2">
          <button
            onClick={handleInstallClick}
            className={`px-4 py-2 rounded-lg font-medium ${
              darkMode ? "bg-blue-500 hover:bg-blue-600" : "bg-blue-600 hover:bg-blue-700"
            } text-white`}
          >
            {deviceType === "mobile" ? "Show Instructions" : "Install App"}
          </button>
          <button
            onClick={handleDismiss}
            className={`px-4 py-2 rounded-lg font-medium ${
              darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            Not Now
          </button>
        </div>
      </div>
    </motion.div>
  );
}
